import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import apiClient from '../lib/api';

const TeamContext = createContext();

export const useTeam = () => {
  const context = useContext(TeamContext);
  if (!context) {
    throw new Error('useTeam must be used within a TeamProvider');
  }
  return context;
};

export const TeamProvider = ({ children }) => {
  const { isAuthenticated, isLoading: authLoading } = useAuth0();
  const [teams, setTeams] = useState([]);
  const [currentTeam, setCurrentTeam] = useState(null);
  const [teamRole, setTeamRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load user's teams
  const loadTeams = useCallback(async () => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    try {
      setError(null);
      const userTeams = await apiClient.getTeams();
      setTeams(userTeams);

      if (userTeams.length > 0) {
        // Restore last selected team from localStorage or use first team
        const savedTeamId = localStorage.getItem('fueldash_current_team');
        const team = userTeams.find(t => t.id === savedTeamId) || userTeams[0];

        setCurrentTeam(team);
        setTeamRole(team.role);
        apiClient.setTeamId(team.id);
      }
    } catch (err) {
      console.error('Error loading teams:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  // Load teams when authentication state changes
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      loadTeams();
    } else if (!authLoading && !isAuthenticated) {
      setLoading(false);
      setTeams([]);
      setCurrentTeam(null);
      setTeamRole(null);
    }
  }, [authLoading, isAuthenticated, loadTeams]);

  // Switch team
  const switchTeam = useCallback(async (teamId) => {
    const team = teams.find(t => t.id === teamId);
    if (!team) {
      console.error('Team not found:', teamId);
      return;
    }

    try {
      // Validate access by calling the switch endpoint
      await apiClient.switchTeam(teamId);

      console.log(`🔄 TeamContext: Setting team to ${team.name} (${teamId})`);
      setCurrentTeam(team);
      setTeamRole(team.role);
      apiClient.setTeamId(teamId);
      console.log(`🔄 TeamContext: apiClient.teamId is now: ${apiClient.getTeamId()}`);
      localStorage.setItem('fueldash_current_team', teamId);

      // Dispatch event so other contexts can reload their data
      console.log(`🔄 TeamContext: Dispatching team-switched event for ${team.name}`);
      window.dispatchEvent(new CustomEvent('team-switched', { detail: { teamId, team } }));
    } catch (err) {
      console.error('Error switching team:', err);
      throw err;
    }
  }, [teams]);

  // Create a new team
  const createTeam = useCallback(async (name, description) => {
    try {
      const newTeam = await apiClient.createTeam({ name, description });
      setTeams(prev => [...prev, newTeam]);

      // Auto-switch to the new team
      setCurrentTeam(newTeam);
      setTeamRole('owner');
      apiClient.setTeamId(newTeam.id);
      localStorage.setItem('fueldash_current_team', newTeam.id);

      window.dispatchEvent(new CustomEvent('team-switched', { detail: { teamId: newTeam.id, team: newTeam } }));

      return newTeam;
    } catch (err) {
      console.error('Error creating team:', err);
      throw err;
    }
  }, []);

  // Update current team
  const updateTeam = useCallback(async (data) => {
    if (!currentTeam) return;

    try {
      const updated = await apiClient.updateTeam(currentTeam.id, data);
      setTeams(prev => prev.map(t => t.id === updated.id ? { ...t, ...updated } : t));
      setCurrentTeam(prev => ({ ...prev, ...updated }));
      return updated;
    } catch (err) {
      console.error('Error updating team:', err);
      throw err;
    }
  }, [currentTeam]);

  // Delete current team
  const deleteTeam = useCallback(async () => {
    if (!currentTeam) return;

    try {
      await apiClient.deleteTeam(currentTeam.id);

      // Remove from teams list
      const remainingTeams = teams.filter(t => t.id !== currentTeam.id);
      setTeams(remainingTeams);

      // Switch to another team
      if (remainingTeams.length > 0) {
        const newTeam = remainingTeams[0];
        setCurrentTeam(newTeam);
        setTeamRole(newTeam.role);
        apiClient.setTeamId(newTeam.id);
        localStorage.setItem('fueldash_current_team', newTeam.id);
        window.dispatchEvent(new CustomEvent('team-switched', { detail: { teamId: newTeam.id, team: newTeam } }));
      } else {
        setCurrentTeam(null);
        setTeamRole(null);
        apiClient.setTeamId(null);
        localStorage.removeItem('fueldash_current_team');
      }
    } catch (err) {
      console.error('Error deleting team:', err);
      throw err;
    }
  }, [currentTeam, teams]);

  // Permission helpers
  const canManageTeam = teamRole === 'owner' || teamRole === 'admin';
  const canEditData = teamRole !== 'viewer';
  const isOwner = teamRole === 'owner';
  const isAdmin = teamRole === 'admin' || teamRole === 'owner';
  const isMember = teamRole === 'member' || isAdmin;
  const isViewer = teamRole === 'viewer';

  const value = {
    // State
    teams,
    currentTeam,
    teamRole,
    loading,
    error,

    // Actions
    loadTeams,
    switchTeam,
    createTeam,
    updateTeam,
    deleteTeam,

    // Permission helpers
    canManageTeam,
    canEditData,
    isOwner,
    isAdmin,
    isMember,
    isViewer,
  };

  return (
    <TeamContext.Provider value={value}>
      {children}
    </TeamContext.Provider>
  );
};

export default TeamContext;
